// minimap.ts — abstract encounter mini-map (node-and-line web, Darkest-Dungeon-inspired).
//
// Browser-owned (owner 2026-09-10). Renders from the graph store (store.ts): explored rooms are lit
// tiles with name labels, unexplored frontier rooms are dim "?" stubs, the party's current room pulses.
// Positions come from layout.ts via the store (deterministic, marker-seeded, remembered per viewer so a
// reveal never reshuffles what you've seen). Nothing here ever asks the server: the graph arrives with the
// page's boot state and WebSocket pushes keep it current; every repaint — a card re-render, a reveal, a
// full-graph push, the GM perspective switch — reads the store.
//
// Interactions: +/− zoom buttons, drag-to-pan, double-click empty space to re-fit; the zoom/pan survives
// repaints. Travel to an explored room: SINGLE-click selects it (revealing a "travel here" button),
// DOUBLE-click (or that button) travels — it traces the shortest all-explored path (direct links +
// inventory passages), flashes it, then hands off to goToEncounter (same handler as the exit list's
// arrow button) so the party member lands there in one move. Repaints wait out that beat. Node tooltips
// ride the site's delegated Bootstrap tooltip system (data-bs-toggle + data-bs-original-title pair).

import { buildMinimapView, getGraph, onMinimapGraphChange, pairKey, type ViewEdge, type ViewNode } from './store';

const SVG_NS = 'http://www.w3.org/2000/svg';
const UNIT = 90;        // px per abstract layout unit (spring rest length)
const TILE = 34;        // node tile size (px, unzoomed)
const PAD = 60;         // fit padding around content (px)

/** A viewer's zoom/pan per mount, kept across repaints until they re-fit. */
const savedViews = new WeakMap<HTMLElement, { x: number; y: number; w: number; h: number }>();

function svgEl<K extends keyof SVGElementTagNameMap>(tag: K, attrs: Record<string, string>): SVGElementTagNameMap[K] {
    const el = document.createElementNS(SVG_NS, tag);
    for (const [key, value] of Object.entries(attrs)) el.setAttribute(key, value);
    return el;
}

/** Two-letter initials badge text, mirroring the thumbnail fallback ("Flooded Cellar" → "FC"). */
function initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter((p) => p.length > 0);
    if (parts.length === 0) return '';
    const first = parts[0] ?? '';
    if (parts.length === 1) return first.slice(0, 2);
    return (first.charAt(0) + (parts[1] ?? '').charAt(0)).toUpperCase();
}

/** A node is a valid travel WAYPOINT/target only if the party has actually been there:
 *  explored, or the current room. Anonymized frontier stubs are never routable — a route may not
 *  pass THROUGH the unknown, and you cannot travel INTO it from the map. */
function isRoutable(n: ViewNode): boolean {
    return (n.current || !!n.explored) && !n.frontier;
}

/**
 * Shortest path (fewest hops) from `fromId` to `toId` over the ROUTABLE subgraph — only explored /
 * current rooms are crossed, and every edge counts equally (direct link or inventory-passage; both
 * are just room↔room connections here). Returns the ordered id list INCLUDING both ends, or null
 * when no all-explored route exists. Deterministic: neighbors are visited in sorted order.
 */
function shortestRoute(
    fromId: string,
    toId: string,
    adj: Record<string, string[]>,
): string[] | null {
    if (fromId === toId) return [fromId];
    const prev: Record<string, string> = {};
    const seen: Record<string, boolean> = { [fromId]: true };
    let queue: string[] = [fromId];
    while (queue.length > 0) {
        const next: string[] = [];
        for (const cur of queue) {
            for (const nb of adj[cur] ?? []) {
                if (seen[nb]) continue;
                seen[nb] = true;
                prev[nb] = cur;
                if (nb === toId) {
                    const path = [toId];
                    let step = toId;
                    while (step !== fromId) {
                        const p = prev[step];
                        if (p === undefined) break;
                        path.push(p);
                        step = p;
                    }
                    return path.reverse();
                }
                next.push(nb);
            }
        }
        queue = next;
    }
    return null;
}

/** Human text for every "no map" outcome — the tab must SAY why there is no mini-map. */
function minimapUnavailableText(reason: string): string {
    switch (reason) {
        case 'no_campaign':
            return 'Mini-map unavailable: this sheet has no campaign context.';
        case 'no_access':
            return 'Mini-map unavailable: you do not have access to this campaign’s map.';
        case 'no_current':
            return 'Mini-map unavailable: no current encounter is set (the party isn’t anywhere yet).';
        case 'no_links':
            return 'Mini-map unavailable: this encounter has no linked encounters to chart. Link encounters together (GM tools) and the web will appear.';
        case 'nothing_explored':
            return 'Mini-map unavailable: nothing around here has been explored or revealed yet.';
        case 'no_map_data':
            return 'Mini-map unavailable: this page carries no map for that campaign.';
        default:
            return 'Mini-map unavailable: no chartable map data was returned.' + (reason ? ` (${reason})` : '');
    }
}

/** Keep the normal map frame present for loading/empty states so XZ rim chrome never floats
 *  over an unanchored blank corner. The message replaces the SVG inside the same frame. */
function renderFrameMessage(host: HTMLElement, className: string, text: string): void {
    const frame = document.createElement('div');
    frame.className = 'ft-minimap ft-minimap--message';
    const div = document.createElement('div');
    div.className = `${className} text-muted fst-italic p-2`;
    div.textContent = text;
    frame.appendChild(div);
    host.replaceChildren(frame);
}

function renderUnavailable(host: HTMLElement, reason: string): void {
    renderFrameMessage(host, 'ft-minimap-unavailable', minimapUnavailableText(reason));
}

/** The mount's CURRENT perspective request ('' = viewer default / GM full web). */
function hostPerspective(host: HTMLElement): string {
    return host.getAttribute('data-perspective') === 'player' ? 'player' : '';
}

/** Flip the mount's perspective and repaint from the store — local. The store holds the raw graph for
 *  everyone (owner ruling: the browser filters), and buildMinimapView only grants the GM view to a
 *  viewer the server flagged as GM. */
function togglePerspective(host: HTMLElement): void {
    if (hostPerspective(host) === 'player') {
        host.removeAttribute('data-perspective');
    } else {
        host.setAttribute('data-perspective', 'player');
    }
    savedViews.delete(host);
    void mountEncounterMinimap(host);
}

/** Small GM⇄player switch appended to the "unavailable" panel — a GM ported into an empty
 *  player view (nothing explored yet) still needs a way back to the full web. */
function appendUnavailableToggle(host: HTMLElement, isGmView: boolean): void {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'btn btn-dark btn-sm ft-minimap-btn ft-minimap-btn--perspective mt-1';
    btn.textContent = isGmView ? 'GM' : 'PC';
    btn.setAttribute('data-bs-toggle', 'tooltip');
    btn.setAttribute('data-bs-original-title', isGmView ? 'Switch to player view' : 'Switch to GM view');
    btn.addEventListener('click', () => togglePerspective(host));
    host.querySelector('.ft-minimap-unavailable')?.appendChild(btn);
}

/** Paint (or repaint) one mount. A request that lands while the mount is painting, or during a travel
 *  beat, is remembered and run once that settles — a pushed reveal is never dropped. */
export async function mountEncounterMinimap(host: HTMLElement): Promise<void> {
    if (host.getAttribute('data-ft-minimap-busy') === '1' || host.getAttribute('data-ft-minimap-hold') === '1') {
        host.setAttribute('data-ft-minimap-repaint', '1');
        return;
    }
    host.setAttribute('data-ft-minimap-busy', '1');
    try {
        await mountEncounterMinimapInner(host);
    } finally {
        host.removeAttribute('data-ft-minimap-busy');
        if (host.getAttribute('data-ft-minimap-repaint') === '1' && host.getAttribute('data-ft-minimap-hold') !== '1') {
            host.removeAttribute('data-ft-minimap-repaint');
            if (host.isConnected) void mountEncounterMinimap(host);
        }
    }
}

async function mountEncounterMinimapInner(host: HTMLElement): Promise<void> {
    const campaignId = host.getAttribute('data-campaign-id') || '';
    if (!checkIfValidUUID(campaignId)) {
        renderUnavailable(host, 'no_campaign');
        return;
    }
    const encounterId = host.getAttribute('data-encounter-id') || '';

    // The page's boot island carries its campaign's graph; pushes keep it current. Nothing to fetch.
    const graph = getGraph(campaignId);
    if (!graph) {
        renderUnavailable(host, 'no_map_data');
        return;
    }

    const perspective = graph.viewerGm && hostPerspective(host) !== 'player' ? 'gm' : 'player';
    const view = buildMinimapView(graph, encounterId, perspective);
    if (view.reason || view.nodes.length === 0) {
        renderUnavailable(host, view.reason);
        if (graph.viewerGm) appendUnavailableToggle(host, view.isGmView);
        return;
    }

    // The room you just walked into may still be a nameless frontier stub until its reveal lands; the
    // mount knows its own room's name.
    const hostName = host.getAttribute('data-encounter-name') || '';
    if (hostName) {
        for (const n of view.nodes) if (n.current && n.name === '') n.name = hostName;
    }

    renderMinimap(host, view.nodes, view.edges, view.isGmView, graph.viewerGm);
}

/** Repaint every live mount of a campaign when its graph changes. Idempotent. */
let repaintInstalled = false;
export function installMinimapRepaint(): void {
    if (repaintInstalled) return;
    repaintInstalled = true;
    onMinimapGraphChange((campaignId) => {
        const mounts = document.querySelectorAll<HTMLElement>('.ft-encounter-minimap-mount[data-ft-minimap-mounted]');
        mounts.forEach((host) => {
            if (!host.isConnected) return;
            if ((host.getAttribute('data-campaign-id') || '').trim().toUpperCase() !== campaignId) return;
            void mountEncounterMinimap(host);
        });
    });
}

function renderMinimap(host: HTMLElement, nodes: ViewNode[], edges: ViewEdge[], isGm: boolean, viewerGm: boolean): void {
    const byId: Record<string, ViewNode> = {};
    for (const n of nodes) byId[n.id] = n;

    const currentId = nodes.find((n) => n.current)?.id ?? '';

    // Adjacency over the ROUTABLE subgraph (explored/current rooms only) — used to trace the most
    // viable path when the player travels to a room they've already been to. Both endpoints must be
    // routable so a route never threads through an anonymized frontier stub.
    const routeAdj: Record<string, string[]> = {};
    for (const n of nodes) if (isRoutable(n)) routeAdj[n.id] = [];
    for (const e of edges) {
        const af = routeAdj[e.from];
        const at = routeAdj[e.to];
        if (af && at) {
            af.push(e.to);
            at.push(e.from);
        }
    }
    for (const list of Object.values(routeAdj)) list.sort();

    // Project abstract units → px and measure content bounds.
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const n of nodes) {
        const px = n.x * UNIT;
        const py = n.y * UNIT;
        minX = Math.min(minX, px); maxX = Math.max(maxX, px);
        minY = Math.min(minY, py); maxY = Math.max(maxY, py);
    }
    const fitBox = {
        x: minX - PAD,
        y: minY - PAD,
        w: (maxX - minX) + PAD * 2,
        h: (maxY - minY) + PAD * 2,
    };
    const saved = savedViews.get(host);
    const view = saved ? { ...saved } : { ...fitBox };

    host.innerHTML = '';
    const frame = document.createElement('div');
    frame.className = 'ft-minimap';

    const svg = svgEl('svg', {
        class: 'ft-minimap-svg',
        viewBox: `${view.x} ${view.y} ${view.w} ${view.h}`,
        preserveAspectRatio: 'xMidYMid meet',
    });

    const gEdges = svgEl('g', { class: 'ft-minimap-edges' });
    const gNodes = svgEl('g', { class: 'ft-minimap-nodes' });
    svg.appendChild(gEdges);
    svg.appendChild(gNodes);

    // "min|max" edge key → its drawn <line>, so a traced route can light its segments.
    const edgeLineByKey: Record<string, SVGLineElement> = {};

    // ── Edges: beaded lines, trimmed back so they don't poke under the tiles ─────────────────────
    for (const e of edges) {
        const a = byId[e.from];
        const b = byId[e.to];
        if (!a || !b) continue;
        const ax = a.x * UNIT, ay = a.y * UNIT;
        const bx = b.x * UNIT, by = b.y * UNIT;
        const dx = bx - ax, dy = by - ay;
        const d = Math.sqrt(dx * dx + dy * dy) || 1;
        const trim = TILE * 0.72;
        const x1 = ax + (dx / d) * trim, y1 = ay + (dy / d) * trim;
        const x2 = bx - (dx / d) * trim, y2 = by - (dy / d) * trim;

        const cls = e.private ? 'ft-minimap-edge ft-minimap-edge--private' : 'ft-minimap-edge';
        const line = svgEl('line', {
            class: cls,
            x1: String(x1), y1: String(y1), x2: String(x2), y2: String(y2),
        });
        edgeLineByKey[pairKey(e.from, e.to)] = line;
        gEdges.appendChild(line);

        // Passage chip (door/grate/stair object realizing the connection) at the midpoint.
        if (e.passage && e.passage.name) {
            const mx = (ax + bx) / 2, my = (ay + by) / 2;
            const chip = svgEl('g', { class: 'ft-minimap-passage' });
            chip.setAttribute('data-bs-toggle', 'tooltip');
            chip.setAttribute('data-bs-original-title', e.passage.name);
            chip.appendChild(svgEl('rect', {
                x: String(mx - 5), y: String(my - 5), width: '10', height: '10',
                transform: `rotate(45 ${mx} ${my})`,
            }));
            gEdges.appendChild(chip);
        }
    }

    // ── Selection + routed travel ───────────────────────────────────────────────────────────────
    // Single-click SELECTS an explored room and reveals its inline travel button; double-click (or
    // that button) TRAVELS. Travel traces the shortest all-explored path to the room, lights it for
    // a beat, then hands off to goToEncounter — the party member lands there in one server move
    // ("jump + show the route"), never re-entering the rooms it passed on the map.
    let selectedId = '';
    let traveling = false;

    const clearSelection = (): void => {
        if (selectedId === '') return;
        gNodes.querySelector('.ft-minimap-node--selected')?.classList.remove('ft-minimap-node--selected');
        selectedId = '';
    };

    const selectNode = (id: string, g: SVGGElement): void => {
        if (selectedId === id) return;
        clearSelection();
        selectedId = id;
        g.classList.add('ft-minimap-node--selected');
    };

    /** Flash the traced route, then travel. `targetId` is always a routable, non-current room. */
    const travelTo = (targetId: string): void => {
        if (traveling || typeof goToEncounter !== 'function') return;
        traveling = true;
        clearSelection();

        // Pushed reveals (another party member exploring) must not repaint this frame out from under
        // the travel beat — hold them, then run whatever arrived once the move settles.
        host.setAttribute('data-ft-minimap-hold', '1');
        // A move that never settles must not freeze this map: the hold lets go after 20 s regardless.
        let safety = 0;
        const release = (): void => {
            window.clearTimeout(safety);
            host.removeAttribute('data-ft-minimap-hold');
            if (host.getAttribute('data-ft-minimap-repaint') === '1') {
                host.removeAttribute('data-ft-minimap-repaint');
                if (host.isConnected) void mountEncounterMinimap(host);
            }
        };
        safety = window.setTimeout(release, 20000);

        // Light every segment + node along the shortest all-explored route (feedback only — the
        // actual move jumps straight to the destination). A disconnected target still travels; it
        // just shows no traced line.
        const lit: Element[] = [];
        const route = shortestRoute(currentId, targetId, routeAdj);
        if (route) {
            for (const id of route) {
                const el = gNodes.querySelector(`[data-ft-node-id="${id}"]`);
                if (el) { el.classList.add('ft-minimap-node--route'); lit.push(el); }
            }
            for (let i = 0; i + 1 < route.length; i++) {
                const a = route[i];
                const b = route[i + 1];
                const seg = a && b ? edgeLineByKey[pairKey(a, b)] : undefined;
                if (seg) { seg.classList.add('ft-minimap-edge--route'); lit.push(seg); }
            }
        }

        // Give the route a brief beat to read, then move. On success goToEncounter re-points the
        // sheet and the minimap re-mounts — but a REFUSED move (e.g. travelling outside a live session:
        // the server warns with HTTP 200 and nothing re-mounts) would otherwise strand `traveling`/the
        // gold route forever. So always release the guard and clear the highlight once the call settles.
        // Calling goToEncounter INSIDE .then() funnels even a synchronous throw into .finally(). If the
        // card itself re-rendered during the beat (this frame is gone), abandon the now-stale travel.
        window.setTimeout(() => {
            if (!frame.isConnected) {
                release();
                return;
            }
            void Promise.resolve()
                .then(() => goToEncounter(targetId))
                .finally(() => {
                    traveling = false;
                    for (const el of lit) {
                        el.classList.remove('ft-minimap-node--route', 'ft-minimap-edge--route');
                    }
                    release();
                });
        }, 650);
    };

    // ── Nodes ─────────────────────────────────────────────────────────────────────────────────────
    for (const n of nodes) {
        const px = n.x * UNIT;
        const py = n.y * UNIT;
        const g = svgEl('g', {
            class: 'ft-minimap-node'
                + (n.current ? ' ft-minimap-node--current' : '')
                + (n.frontier || !n.explored ? ' ft-minimap-node--frontier' : ''),
            transform: `translate(${px} ${py})`,
            'data-ft-node-id': n.id,
        });

        if (n.current) {
            // Pulsing "you are here" ring behind the tile.
            g.appendChild(svgEl('rect', {
                class: 'ft-minimap-pulse',
                x: String(-TILE / 2 - 6), y: String(-TILE / 2 - 6),
                width: String(TILE + 12), height: String(TILE + 12),
                rx: '10',
            }));
        }

        g.appendChild(svgEl('rect', {
            class: 'ft-minimap-tile',
            x: String(-TILE / 2), y: String(-TILE / 2),
            width: String(TILE), height: String(TILE),
            rx: '7',
        }));

        const glyph = svgEl('text', {
            class: 'ft-minimap-glyph',
            x: '0', y: '1',
            'text-anchor': 'middle',
            'dominant-baseline': 'central',
        });
        glyph.textContent = (n.frontier || !n.explored) && !n.current && n.name === ''
            ? '?'
            : initials(n.name) || '?';
        g.appendChild(glyph);

        if (n.name !== '') {
            const label = svgEl('text', {
                class: 'ft-minimap-label',
                x: '0', y: String(TILE / 2 + 14),
                'text-anchor': 'middle',
            });
            label.textContent = n.name;
            g.appendChild(label);
            g.setAttribute('data-bs-toggle', 'tooltip');
            g.setAttribute('data-bs-original-title', n.name + (n.explored ? '' : ' (unexplored)'));
        } else {
            g.setAttribute('data-bs-toggle', 'tooltip');
            g.setAttribute('data-bs-original-title', 'Unexplored');
        }

        // An explored (or GM-visible) room the party isn't already in is a travel target: single-
        // click SELECTS it (revealing the travel button below), double-click TRAVELS.
        if (isRoutable(n) && !n.current) {
            g.classList.add('ft-minimap-node--clickable');
            const targetId = n.id;

            g.addEventListener('click', (ev) => {
                if (frame.getAttribute('data-ft-panned') === '1') return; // was a drag-pan, not a click
                ev.stopPropagation();
                selectNode(targetId, g);
            });
            g.addEventListener('dblclick', (ev) => {
                ev.stopPropagation(); // don't let it bubble to the svg's dbl-click-to-refit
                travelTo(targetId);
            });

            // Inline travel button floating above the tile; hidden until the node is selected (CSS
            // gates on --selected). Clicking it travels — same as a double-click.
            const btn = svgEl('g', { class: 'ft-minimap-travel-btn' });
            btn.setAttribute('data-bs-toggle', 'tooltip');
            btn.setAttribute('data-bs-original-title', 'Travel here');
            btn.appendChild(svgEl('circle', {
                class: 'ft-minimap-travel-btn-bg',
                cx: '0', cy: String(-TILE / 2 - 16), r: '11',
            }));
            const btnGlyph = svgEl('text', {
                class: 'ft-minimap-travel-btn-glyph',
                x: '0', y: String(-TILE / 2 - 16),
                'text-anchor': 'middle', 'dominant-baseline': 'central',
            });
            btnGlyph.textContent = '➜'; // heavy round-tip rightward arrow — "go here"
            btn.appendChild(btnGlyph);
            btn.addEventListener('click', (ev) => {
                if (frame.getAttribute('data-ft-panned') === '1') return; // trailing click from a drag-pan
                ev.stopPropagation();
                travelTo(targetId);
            });
            g.appendChild(btn);
        }

        gNodes.appendChild(g);
    }

    // ── Zoom / pan (kept across repaints via savedViews; a re-fit forgets it) ─────────────────────
    const applyView = () => svg.setAttribute('viewBox', `${view.x} ${view.y} ${view.w} ${view.h}`);

    const zoomAt = (factor: number) => {
        const cx = view.x + view.w / 2;
        const cy = view.y + view.h / 2;
        view.w = Math.max(UNIT, Math.min(fitBox.w * 3, view.w * factor));
        view.h = Math.max(UNIT, Math.min(fitBox.h * 3, view.h * factor));
        view.x = cx - view.w / 2;
        view.y = cy - view.h / 2;
        applyView();
        savedViews.set(host, { ...view });
    };

    const refit = () => {
        view.x = fitBox.x; view.y = fitBox.y; view.w = fitBox.w; view.h = fitBox.h;
        applyView();
        savedViews.delete(host);
    };

    // Drag to pan (pointer events; passive by design — no preventDefault on pointerdown, per the
    // press-hold system's rules).
    let dragging = false;
    let lastX = 0, lastY = 0;
    svg.addEventListener('pointerdown', (ev) => {
        dragging = true;
        frame.setAttribute('data-ft-panned', '0');
        lastX = ev.clientX; lastY = ev.clientY;
    });
    svg.addEventListener('pointermove', (ev) => {
        if (!dragging) return;
        const rect = svg.getBoundingClientRect();
        const scale = view.w / rect.width;
        const dx = (ev.clientX - lastX) * scale;
        const dy = (ev.clientY - lastY) * scale;
        if (Math.abs(ev.clientX - lastX) + Math.abs(ev.clientY - lastY) > 3) {
            frame.setAttribute('data-ft-panned', '1');
            // Capturing on pointerdown retargets the ensuing click/dblclick to the SVG, so room
            // nodes never receive genuine mouse double-clicks. Capture only once this is a drag.
            if (!svg.hasPointerCapture(ev.pointerId)) svg.setPointerCapture(ev.pointerId);
        }
        view.x -= dx; view.y -= dy;
        lastX = ev.clientX; lastY = ev.clientY;
        applyView();
        savedViews.set(host, { ...view });
    });
    const endDrag = () => { dragging = false; };
    svg.addEventListener('pointerup', endDrag);
    svg.addEventListener('pointercancel', endDrag);
    svg.addEventListener('dblclick', refit);

    // A click that reaches the svg came from empty space (node clicks stopPropagation) — drop the
    // current selection, unless it was really the tail of a drag-pan.
    svg.addEventListener('click', () => {
        if (frame.getAttribute('data-ft-panned') === '1') return;
        clearSelection();
    });

    // Controls (WoW-style +/− plus a re-center) on the frame rim.
    const controls = document.createElement('div');
    controls.className = 'ft-minimap-controls';
    const mkBtn = (label: string, title: string, fn: () => void): HTMLButtonElement => {
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'btn btn-dark btn-sm ft-minimap-btn';
        b.textContent = label;
        b.setAttribute('aria-label', title);
        b.setAttribute('data-bs-toggle', 'tooltip');
        b.setAttribute('data-bs-original-title', title);
        b.addEventListener('click', fn);
        return b;
    };
    controls.appendChild(mkBtn('+', 'Zoom in', () => zoomAt(1 / 1.35)));
    controls.appendChild(mkBtn('−', 'Zoom out', () => zoomAt(1.35)));
    controls.appendChild(mkBtn('◎', 'Re-center', refit));
    if (viewerGm) {
        // GM⇄player perspective switch (rim control, WoW/BG3 style). Label shows the CURRENT
        // view; the tooltip names the switch target. Local — the GM already holds the raw graph.
        const persp = mkBtn(isGm ? 'GM' : 'PC', isGm ? 'Switch to player view' : 'Switch to GM view', () => togglePerspective(host));
        persp.classList.add('ft-minimap-btn--perspective');
        controls.appendChild(persp);
    }

    frame.appendChild(svg);
    frame.appendChild(controls);
    host.appendChild(frame);
}
